import { useMemo, useRef, useState } from 'react';
import type { CreateWorkspaceInput, WorkspaceKind, WorkspaceSummary } from '../../shared/workspaces';
import { useI18n } from '../i18n';
import { localizedError } from '../i18n/errors';
import { AdaptiveSheet } from './AdaptiveSheet';
import { UiIcon } from './ActionIcon';

interface Props {
  mode: 'desktop' | 'mobile';
  workspaces: WorkspaceSummary[];
  currentWorkspaceId: string;
  canCreate: boolean;
  onSwitch: (workspaceId: string) => Promise<void> | void;
  onCreate: (input: CreateWorkspaceInput) => Promise<void>;
}

function requestKey(): string {
  return `workspace-create:${crypto.randomUUID()}`;
}

export function WorkspaceSwitcher({ mode, workspaces, currentWorkspaceId, canCreate, onSwitch, onCreate }: Props) {
  const { locale } = useI18n();
  const current = useMemo(() => workspaces.find(item => item.id === currentWorkspaceId) ?? workspaces[0], [workspaces, currentWorkspaceId]);
  const desktopDetails = useRef<HTMLDetailsElement>(null);
  const createRequestKey = useRef(requestKey());
  const [switchOpen, setSwitchOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<WorkspaceKind>('restaurant');
  const [creating, setCreating] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  if (!current) return null;

  const kindLabel = (value: WorkspaceKind) => {
    if (locale === 'en') return value === 'restaurant' ? 'Restaurant' : value === 'rnd_kitchen' ? 'R&D Kitchen' : 'Test Kitchen';
    return value === 'restaurant' ? '餐厅' : value === 'rnd_kitchen' ? '研发厨房' : '测试厨房';
  };

  async function choose(workspaceId: string) {
    if (workspaceId === currentWorkspaceId || switchingId) return;
    if (desktopDetails.current) desktopDetails.current.open = false;
    setSwitchingId(workspaceId);
    setError('');
    try {
      await onSwitch(workspaceId);
      setSwitchOpen(false);
    } catch (cause) {
      setError(localizedError(cause, locale, {
        'zh-CN': '切换空间失败，请重试。',
        en: 'Failed to switch Workspace. Please retry.'
      }));
    } finally {
      setSwitchingId(null);
    }
  }

  function openCreate() {
    if (!canCreate) return;
    if (desktopDetails.current) desktopDetails.current.open = false;
    setError('');
    setName('');
    setKind('restaurant');
    createRequestKey.current = requestKey();
    setCreateOpen(true);
  }

  function updateName(value: string) {
    setName(value);
    if (!creating) createRequestKey.current = requestKey();
  }

  function updateKind(value: WorkspaceKind) {
    setKind(value);
    if (!creating) createRequestKey.current = requestKey();
  }

  async function submitCreate(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim() || creating || !canCreate) return;
    setCreating(true);
    setError('');
    try {
      await onCreate({ name: name.trim(), kind, requestKey: createRequestKey.current });
      setCreateOpen(false);
    } catch (cause) {
      setError(localizedError(cause, locale, {
        'zh-CN': '创建空间失败，请重试。',
        en: 'Failed to create the Workspace. Please retry.'
      }));
    } finally {
      setCreating(false);
    }
  }

  const rows = <>
    <div className="workspace-switcher-heading">{locale === 'zh-CN' ? '切换空间' : 'Switch Workspace'}</div>
    <div className="workspace-switcher-list">
      {workspaces.map(item => <button
        key={item.id}
        type="button"
        className={`workspace-switcher-row ${item.id === current.id ? 'active' : ''}`}
        disabled={item.membershipStatus !== 'active' || item.status !== 'active' || Boolean(switchingId)}
        aria-busy={switchingId === item.id || undefined}
        onClick={() => { choose(item.id).catch(console.error); }}
      >
        <span className="workspace-mark" aria-hidden="true">{item.identityMark || item.name.slice(0, 1)}</span>
        <span className="workspace-row-copy"><strong>{item.name}</strong><small>{switchingId === item.id
          ? (locale === 'zh-CN' ? '正在切换…' : 'Switching…')
          : `${item.roles.join(' · ') || (locale === 'zh-CN' ? '无权限' : 'No access')} · ${item.memberCount} ${locale === 'zh-CN' ? '人' : 'members'}`}</small></span>
        <span className="workspace-check" aria-hidden="true">{item.id === current.id ? '✓' : ''}</span>
      </button>)}
    </div>
    {error && switchOpen && <p className="workspace-dialog-error" role="alert">{error}</p>}
    {canCreate && <button type="button" className="workspace-create-link" disabled={Boolean(switchingId)} onClick={openCreate}><UiIcon name="add" size={16} /><span>{locale === 'zh-CN' ? '新建空间' : 'New Workspace'}</span></button>}
  </>;

  return <>
    {mode === 'desktop' ? <details ref={desktopDetails} className="workspace-switcher desktop-workspace-switcher">
      <summary aria-label={locale === 'zh-CN' ? '切换空间' : 'Switch Workspace'}>
        <span className="workspace-mark" aria-hidden="true">{current.identityMark || current.name.slice(0, 1)}</span>
        <span className="workspace-current-copy"><strong>{current.name}</strong><small>{current.roles.join(' · ') || (locale === 'zh-CN' ? '无权限' : 'No access')}</small></span>
        <span className="workspace-switcher-chevron" aria-hidden="true"><UiIcon name="chevronDown" size={15} /></span>
      </summary>
      <div className="workspace-switcher-popover">{rows}</div>
    </details> : <>
      <button type="button" className="mobile-workspace-trigger" onClick={() => setSwitchOpen(true)} aria-label={locale === 'zh-CN' ? '切换空间' : 'Switch Workspace'}>
        <span className="workspace-mark mobile-workspace-mark" aria-hidden="true">{current.identityMark || current.name.slice(0, 1)}</span>
        <span className="mobile-workspace-copy"><small>{locale === 'zh-CN' ? '当前空间' : 'Current workspace'}</small><strong>{current.name}</strong></span>
        <span className="mobile-workspace-switch-copy"><span>{locale === 'zh-CN' ? '切换' : 'Switch'}</span><UiIcon name="chevronDown" size={14} /></span>
      </button>
      <AdaptiveSheet
        open={switchOpen}
        eyebrow={locale === 'zh-CN' ? '餐厅 / 厨房空间' : 'Restaurant / kitchen workspace'}
        title={locale === 'zh-CN' ? '切换当前空间' : 'Switch workspace'}
        closeLabel={locale === 'zh-CN' ? '关闭空间切换' : 'Close workspace switcher'}
        onDismiss={() => { if (!switchingId) setSwitchOpen(false); }}
      >
        <div className="workspace-switch-sheet-body">{rows}</div>
      </AdaptiveSheet>
    </>}

    <AdaptiveSheet
      open={createOpen}
      eyebrow={locale === 'zh-CN' ? '空间管理' : 'Workspace'}
      title={locale === 'zh-CN' ? '新建空间' : 'Create workspace'}
      closeLabel={locale === 'zh-CN' ? '关闭新建空间' : 'Close create workspace'}
      onDismiss={() => { if (!creating) setCreateOpen(false); }}
    >
      <form className="workspace-create-sheet-form" onSubmit={submitCreate}>
        <label>{locale === 'zh-CN' ? '空间名称' : 'Workspace name'}<input autoFocus disabled={creating} value={name} maxLength={120} onChange={event => updateName(event.target.value)} placeholder={locale === 'zh-CN' ? '例如：研发厨房' : 'e.g. R&D Kitchen'} /></label>
        <label>{locale === 'zh-CN' ? '类型' : 'Type'}<select disabled={creating} value={kind} onChange={event => updateKind(event.target.value as WorkspaceKind)}>
          {(['restaurant', 'rnd_kitchen', 'test_kitchen'] as WorkspaceKind[]).map(value => <option key={value} value={value}>{kindLabel(value)}</option>)}
        </select></label>
        {error && <p className="workspace-dialog-error" role="alert">{error}</p>}
        <p className="workspace-dialog-note">{locale === 'zh-CN' ? '创建后你会成为主管理员，并自动进入新空间。网络失败可直接重试，不会重复创建。' : 'After creation you become Owner and switch into the empty Workspace. If the network fails, retrying the same submission will not create a duplicate.'}</p>
        <div className="workspace-dialog-actions"><button type="button" disabled={creating} onClick={() => window.history.back()}>{locale === 'zh-CN' ? '取消' : 'Cancel'}</button><button type="submit" disabled={creating || !name.trim()}>{creating ? (locale === 'zh-CN' ? '创建中…' : 'Creating…') : (locale === 'zh-CN' ? '创建' : 'Create')}</button></div>
      </form>
    </AdaptiveSheet>
  </>;
}
